import styled from '@emotion/styled';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { GridCellProps } from 'react-virtualized';
import { downloadPaymentInvoice } from '~/api';
import { CELL_SETTINGS } from '~/components/MultiGrid/constants';
import * as MultiGridStyles from '~/components/MultiGrid/styles';
import Text from '~/components/Text';
import DownloadIcon from '~/icons/DownloadIcon';
import { BILLING_ALL_FIELDS, BILLING_FIELDS_ORDER } from './constants';
import { useBillingContext } from './hooks/useBillingContext';
import { analytics, ANALYTICS_EVENTS } from '~/services/analyticsService';

const SubscriptionWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0;
`;

const StatusBadge = styled.span<{ status: 'paid' | 'pending' | 'failed' | 'succeeded' }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;

  ${props => {
    switch (props.status) {
      case 'paid':
      case 'succeeded':
        return `
          background: #e8f5e8;
          color: #2d7d32;
          &:before {
            content: '✓';
            margin-right: 4px;
          }
        `;
      case 'pending':
        return `
          background: #fff3e0;
          color: #f57c00;
        `;
      case 'failed':
        return `
          background: #ffebee;
          color: #d32f2f;
        `;
      default:
        return `
          background: var(--neut7);
          color: var(--neut3);
        `;
    }
  }}
`;

const ActionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const DownloadButton = styled.button<{ isLoading?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: var(--neut6);
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: ${props => (props.isLoading ? 0.7 : 1)};

  &:hover {
    background: var(--neut5);
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

interface BillingGridCellProps {
  gridProps: GridCellProps;
  cellProps: {
    multiGridSettings: Readonly<{
      fixedRowCount: number;
      fixedColumnCount: number;
    }>;
  };
}

const BillingGridCell: React.FC<BillingGridCellProps> = ({
  gridProps: { columnIndex, rowIndex, style },
  cellProps,
}) => {
  const { listIds, listData } = useBillingContext();
  const { multiGridSettings } = cellProps;
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  const thisBillingItem = listData[listIds[rowIndex - 1]];
  const field = BILLING_ALL_FIELDS[BILLING_FIELDS_ORDER[columnIndex]];
  const keyName = field.value;

  const rowIndexType = useMemo(() => {
    if (rowIndex === 0) {
      return 'first';
    }
    return rowIndex === listIds.length ? 'last' : null;
  }, [rowIndex, listIds.length]);

  const columnIndexType = useMemo(() => {
    if (columnIndex === 0) {
      return 'first';
    }
    return columnIndex === BILLING_FIELDS_ORDER.length - 1 ? 'last' : null;
  }, [columnIndex]);

  const isLastStaticColumn = useMemo(
    () => columnIndex === multiGridSettings.fixedColumnCount - 1,
    [columnIndex, multiGridSettings.fixedColumnCount],
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatAmount = (amount: string) => {
    const numAmount = parseFloat(amount);
    return `$${numAmount.toFixed(2)}`;
  };

  const formatInvoiceId = (id: number, billingDate: string) => {
    const date = new Date(billingDate);
    const monthYear = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    return `Invoice #${id} - ${monthYear}`;
  };

  const formatPlanName = (planName: string) => {
    return planName.charAt(0).toUpperCase() + planName.slice(1).toLowerCase();
  };

  const isPaidStatus = (status: string) => {
    return status.toLowerCase().includes('succeeded') || status.toLowerCase().includes('paid');
  };

  const handleDownloadInvoice = async (paymentId: string) => {
    // Only allow download for successful payments
    if (!isPaidStatus(thisBillingItem.status)) {
      return;
    }

    analytics.capture(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      screen_name: 'Plans',
      button_name: 'Download Invoice',
      button_id: 'billing_invoice_download_cta',
    });

    setDownloadingIds(prev => new Set(prev).add(paymentId));

    try {
      const blob = await downloadPaymentInvoice(paymentId);

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${paymentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error downloading invoice, please contact support team');
    } finally {
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(paymentId);
        return newSet;
      });
    }
  };

  const renderCellContent = () => {
    if (!thisBillingItem) return null;

    switch (keyName) {
      case 'subscription_id':
        return (
          <SubscriptionWrapper>
            <Text size="sm">
              {formatInvoiceId(thisBillingItem.id, thisBillingItem.billing_date)}
            </Text>
          </SubscriptionWrapper>
        );

      case 'billing_date':
        return <Text size="sm">{formatDate(thisBillingItem.billing_date)}</Text>;

      case 'expiry_date':
        return (
          <Text size="sm">
            {thisBillingItem.expiry_date ? formatDate(thisBillingItem.expiry_date) : '--'}
          </Text>
        );

      case 'status':
        const displayStatus = isPaidStatus(thisBillingItem.status) ? 'Paid' : thisBillingItem.status;
        return <StatusBadge status={thisBillingItem.status}>{displayStatus}</StatusBadge>;

      case 'amount':
        return (
          <Text size="sm" className="bold">
            {formatAmount(thisBillingItem.amount)}
          </Text>
        );

      case 'plan_name':
        return <Text size="sm">{formatPlanName(thisBillingItem.plan_name)}</Text>;

      case 'actions':
        const isDownloading = downloadingIds.has(thisBillingItem.id.toString());
        const isDisabled = !isPaidStatus(thisBillingItem.status);

        if (isDisabled) {
          return null;
        }

        return (
          <ActionWrapper>
            <DownloadButton
              onClick={() => handleDownloadInvoice(thisBillingItem.payment_id)}
              disabled={isDisabled || isDownloading}
              isLoading={isDownloading}
              title={
                isDisabled ? 'Invoice only available for successful payments' : 'Download Invoice'
              }
            >
              {isDownloading ? <LoadingSpinner /> : <DownloadIcon width={16} height={16} />}
            </DownloadButton>
          </ActionWrapper>
        );

      default:
        return null;
    }
  };

  if (rowIndex === 0) {
    return (
      <MultiGridStyles.HeaderCell
        columnIndexType={columnIndexType}
        style={style}
        {...{ [CELL_SETTINGS.lastColumnDataAttribute]: isLastStaticColumn }}
      >
        <div style={{ padding: '12px 16px' }}>
          <Text size="sm" className="bold" color="var(--neut2)">
            {field.label}
          </Text>
        </div>
        {isLastStaticColumn && <MultiGridStyles.AbsoluteBorder />}
      </MultiGridStyles.HeaderCell>
    );
  }

  if (thisBillingItem) {
    return (
      <MultiGridStyles.BodyCell
        className="align-center"
        rowIndexType={rowIndexType}
        columnIndexType={columnIndexType}
        paddingLayer={field.isCellFullWidth ? 'first_child' : 'self'}
        style={style}
        {...{ [CELL_SETTINGS.lastColumnDataAttribute]: isLastStaticColumn }}
      >
        {renderCellContent()}
        {isLastStaticColumn && <MultiGridStyles.AbsoluteBorder />}
      </MultiGridStyles.BodyCell>
    );
  }

  return null;
};

export default BillingGridCell;
